import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { formatMoney } from '../../owners/formatMoney';

const { rootUrl } = require('../../../../config/config');

const CarDrivers = ({ plate_no }) => {
    const [allocations, setAllocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [driversMap, setDriversMap] = useState({});

    useEffect(() => {
        if (!plate_no) return;
        setLoading(true);

        const fetchData = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const res = await axios.get(`${rootUrl}/cars/drivers/plate/${encodeURIComponent(plate_no)}`, {
                    headers: { Authorization: token ? `Bearer ${token}` : '' }
                });

                let data = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);

                // Sort allocations so the latest allocation (by start/allocation date) appears first
                const getStartDate = (a) => a?.start_date || a?.allocation_date || a?.input_date || null;
                data = (data || []).slice().sort((a, b) => {
                    const da = new Date(getStartDate(a));
                    const db = new Date(getStartDate(b));
                    if (isNaN(da) && isNaN(db)) return 0;
                    if (isNaN(da)) return 1;
                    if (isNaN(db)) return -1;
                    return db - da; // newest first
                });

                setAllocations(data || []);

                // Fetch driver details for unique driver IDs (cache them)
                const ids = [...new Set(data.map(a => a.driver_id).filter(Boolean))];
                const map = {};
                await Promise.all(ids.map(async (id) => {
                    try {
                        const r = await axios.get(`${rootUrl}/drivers/id_no/${id}`, {
                            headers: { Authorization: token ? `Bearer ${token}` : '' }
                        });
                        map[id] = r.data || null;
                    } catch (err) {
                        map[id] = null;
                    }
                }));

                setDriversMap(map);
                setLoading(false);
            } catch (err) {
                setError(err.response?.data?.message || err.message || 'Failed to load allocations');
                setLoading(false);
            }
        };

        fetchData();
    }, [plate_no]);

    const formatDate = (s) => {
        if (!s) return '-';
        try {
            const d = new Date(s);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch { return s; }
    };

    const isNumeric = (v) => {
        if (v === null || v === undefined || v === '') return false;
        return !Number.isNaN(Number(String(v).replace(/,/g, '')));
    };

    const timespan = (startRaw, endRaw) => {
        try {
            const start = new Date(startRaw);
            const end = endRaw ? new Date(endRaw) : new Date();
            const diff = Math.abs(end - start);
            const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
            if (days < 30) return `${days} day${days === 1 ? '' : 's'}`;
            const months = Math.floor(days / 30);
            if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
            const years = (months / 12).toFixed(1);
            return `${years} yrs`;
        } catch {
            return '-';
        }
    };

    // Summary helpers
    const totalDrivers = () => {
        const ids = allocations.map(a => a.driver_id || a.driver || a.driverId).filter(Boolean);
        return new Set(ids).size;
    };

    const calculateTotalWorkingDays = () => {
        const today = new Date();
        let total = 0;
        allocations.forEach(a => {
            const startRaw = a.start_date || a.allocation_date || a.input_date || a.assigned_on;
            if (!startRaw) return;
            const start = new Date(startRaw);
            if (isNaN(start)) return;
            const end = a.end_date ? new Date(a.end_date) : today;
            if (isNaN(end)) return;
            const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
            if (!Number.isNaN(diff) && diff > 0) total += diff;
        });
        return total;
    };

    const formatDuration = (days) => {
        if (!days) return '0 days';
        const years = Math.floor(days / 365);
        let rem = days % 365;
        const weeks = Math.floor(rem / 7);
        const remDays = rem % 7;
        const parts = [];
        if (years) parts.push(`${years}y`);
        if (weeks) parts.push(`${weeks}w`);
        if (remDays) parts.push(`${remDays}d`);
        return parts.join(' ') || `${days}d`;
    };

    if (loading) return (
        <div className="w-full flex items-center justify-center py-8">
            <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-[#FF5733]" />
        </div>
    );

    if (error) return (
        <div className="p-4 bg-white rounded-xl shadow-none border border-gray-100">
            <p className="text-red-600">{error}</p>
        </div>
    );

    return (
        <div>
            {/* Summary section: total drivers, total working duration, manage allocations */}
            <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-white/80 rounded-sm shadow-none border border-gray-100 text-center">
                    <div className="text-xs text-gray-500">Total Drivers</div>
                    <div className="text-sm font-semibold text-[#2a2e30]">{totalDrivers()}</div>
                </div>
                <div className="p-4 bg-white/80 rounded-sm shadow-none border border-gray-100 text-center">
                    <div className="text-xs text-gray-500">Total Working Duration</div>
                    <div className="text-sm font-semibold text-[#2a2e30]">{formatDuration(calculateTotalWorkingDays())}</div>
                    <div className="text-xs text-gray-500 mt-1">{calculateTotalWorkingDays()} days</div>
                </div>
                <div className="flex items-center justify-center">
                    <a
                        href={`/cars/drivers/${encodeURIComponent(plate_no || 'KDK507Z')}`}
                        className="inline-flex items-center px-3 py-2 border border-[#2a2e30] text-[#2a2e30] text-sm rounded-sm hover:bg-[#2a2e30]/10"
                    >
                        Manage Allocations
                    </a>
                </div>
            </div>
            {allocations.length === 0 ? (
                <div className="p-4 bg-white rounded-xl shadow-none border border-gray-100 text-gray-600">No driver allocations found for this car.</div>
            ) : (
                <div>
                    {/* Mobile Card Layout */}
                    <div className="md:hidden space-y-4">
                        {allocations.map((a) => {
                            const driver = driversMap[a.driver_id] || {};
                            const balance = driver?.actual_bal ?? (driver?.bal ?? 'N/A');
                            const licenseNo = a.license_no || driver?.license_no;
                            const stickerLink = a.verified_sticker_link || a.license_sticker_link || driver?.license_sticker_link;
                            const verStatus = a.verification_status;

                            return (
                                <div key={a.allocation_number || a.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                                    {/* Driver Name & ID */}
                                    <div className="flex items-start justify-between mb-3 pb-3 border-b border-gray-100">
                                        <div className="flex-1">
                                            {(a.driver_id || driver?.id) ? (
                                                <Link to={`/drivers/${a.driver_id || driver?.id}`} className="text-base font-semibold text-[#2a2e30] hover:underline">
                                                    {a.driver_name || driver?.name || `Driver ${a.driver_id}`}
                                                </Link>
                                            ) : (
                                                <div className="text-base font-semibold text-[#2a2e30]">{a.driver_name || driver?.name || `Driver ${a.driver_id}`}</div>
                                            )}
                                            <div className="text-xs text-gray-500 mt-1">{driver?.email || ''}</div>
                                            {a.driver_id && (
                                                <Link to={`/drivers/${a.driver_id}`} className="text-xs text-blue-600 hover:underline">
                                                    ID: {a.driver_id}
                                                </Link>
                                            )}
                                        </div>
                                        <div className="text-sm font-medium text-[#FF5733]">{timespan(a.start_date || a.allocation_date, a.end_date)}</div>
                                    </div>

                                    {/* Grid Info */}
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <div className="text-xs text-gray-500 mb-1">License</div>
                                            <div className="font-medium text-gray-800">{licenseNo || '-'}</div>
                                            {verStatus && (
                                                <span className={`inline-block text-xs px-2 py-0.5 rounded-full mt-1 ${verStatus.toLowerCase() === 'verified' ? 'bg-green-100 text-green-800' :
                                                    verStatus.toLowerCase() === 'failed' ? 'bg-red-100 text-red-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {verStatus}
                                                </span>
                                            )}
                                            {stickerLink && (
                                                <a
                                                    href={stickerLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block text-xs text-blue-600 hover:underline mt-1"
                                                >
                                                    View Sticker
                                                </a>
                                            )}
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500 mb-1">Phone</div>
                                            <div className="font-medium text-gray-800">{driver?.phone || '-'}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500 mb-1">Start Date</div>
                                            <div className="font-medium text-gray-800">{formatDate(a.start_date || a.allocation_date)}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500 mb-1">End Date</div>
                                            <div className="font-medium text-gray-800">{a.end_date ? formatDate(a.end_date) : <span className="text-green-600">Active</span>}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500 mb-1">Weekly Amount</div>
                                            <div className="font-medium text-gray-800">{a.weekly_amount != null ? formatMoney(a.weekly_amount) : '-'}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500 mb-1">Car WAR</div>
                                            <div className="font-medium text-gray-800">{isNumeric(a.car_war_sum) ? formatMoney(a.car_war_sum) : '-'}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500 mb-1">Driver WAR</div>
                                            <div className="font-medium text-gray-800">{isNumeric(a.driver_war_sum) ? formatMoney(a.driver_war_sum) : '-'}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500 mb-1">Driver Balance</div>
                                            <div className="font-medium text-gray-800">{balance != null && isNumeric(balance) ? formatMoney(balance) : (balance ?? 'N/A')}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Desktop Table Layout */}
                    <div className="hidden md:block overflow-x-auto">
                        <div className="border border-gray-300 rounded-sm overflow-hidden">
                        <table className="min-w-full">
                            <thead>
                                <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                                    <th className="px-2 py-2 text-left text-xs font-semibold text-gray-800 leading-none">Driver</th>
                                    <th className="px-2 py-2 text-left text-xs font-semibold text-gray-800 leading-none">ID</th>
                                    <th className="px-2 py-2 text-left text-xs font-semibold text-gray-800 leading-none">License</th>
                                    <th className="px-2 py-2 text-left text-xs font-semibold text-gray-800 leading-none">Phone</th>
                                    <th className="px-2 py-2 text-left text-xs font-semibold text-gray-800 leading-none">Start</th>
                                    <th className="px-2 py-2 text-left text-xs font-semibold text-gray-800 leading-none">End</th>
                                    <th className="px-2 py-2 text-left text-xs font-semibold text-gray-800 leading-none">Duration</th>
                                    <th className="px-2 py-2 text-right text-xs font-semibold text-gray-800 leading-none">Weekly</th>
                                    <th className="px-2 py-2 text-right text-xs font-semibold text-gray-800 leading-none">Car WAR</th>
                                    <th className="px-2 py-2 text-right text-xs font-semibold text-gray-800 leading-none">Driver WAR</th>
                                    <th className="px-2 py-2 text-right text-xs font-semibold text-gray-800 leading-none">Balance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {allocations.map((a, idx) => {
                                    const driver = driversMap[a.driver_id] || {};
                                    const balance = driver?.actual_bal ?? (driver?.bal ?? null);
                                    const licenseNo = a.license_no || driver?.license_no;
                                    const stickerLink = a.verified_sticker_link || a.license_sticker_link || driver?.license_sticker_link;
                                    const verStatus = a.verification_status;

                                    return (
                                        <tr key={a.allocation_number || a.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors duration-150`}>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs">
                                                {(a.driver_id || driver?.id) ? (
                                                    <div>
                                                        <Link to={`/drivers/${a.driver_id || driver?.id}`} className="font-semibold text-[#2a2e30] hover:underline">{a.driver_name || driver?.name || `Driver ${a.driver_id}`}</Link>
                                                        <div className="text-[10px] text-gray-500">{driver?.email || ''}</div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div className="font-semibold text-[#2a2e30]">{a.driver_name || driver?.name || `Driver ${a.driver_id}`}</div>
                                                        <div className="text-[10px] text-gray-500">{driver?.email || ''}</div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-700">{a.driver_id ? (<Link to={`/drivers/${a.driver_id}`} className="text-[#FF5733] hover:underline">{a.driver_id}</Link>) : '-'}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs">
                                                <div className="flex flex-col gap-0.5">
                                                    <span className="text-gray-700">{licenseNo || '-'}</span>
                                                    {verStatus && (
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full w-fit ${verStatus.toLowerCase() === 'verified' ? 'bg-green-100 text-green-800' : verStatus.toLowerCase() === 'failed' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                                                            {verStatus}
                                                        </span>
                                                    )}
                                                    {stickerLink && (
                                                        <a href={stickerLink} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#FF5733] hover:underline">View Sticker</a>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-700">{driver?.phone || '-'}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-700">{formatDate(a.start_date || a.allocation_date)}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs">{a.end_date ? <span className="text-gray-700">{formatDate(a.end_date)}</span> : <span className="text-green-600 font-medium">Active</span>}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs text-[#FF5733] font-medium">{timespan(a.start_date || a.allocation_date, a.end_date)}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs text-right text-gray-800 font-medium">{a.weekly_amount != null ? formatMoney(a.weekly_amount) : '-'}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs text-right text-gray-800 font-medium">{isNumeric(a.car_war_sum) ? formatMoney(a.car_war_sum) : '-'}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs text-right text-gray-800 font-medium">{isNumeric(a.driver_war_sum) ? formatMoney(a.driver_war_sum) : '-'}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs text-right text-gray-800 font-medium">{balance != null && isNumeric(balance) ? formatMoney(balance) : (balance ?? 'N/A')}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CarDrivers;
